import { Avatar, Button, Flex, Space, Table, Typography } from "antd";
import { debounce } from "lodash-es";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useLocation, useParams } from "react-router-dom";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent.jsx";
import PermissionCheck from "../../Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import { useDeleteTeam, useFetchTeams } from "../../entities/api";
import { PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "~hooks/useHandlePagination.js";
import usePermissionsByCompetitionName from "../../hooks/usePermissionsByCompetitionName";
import { useSportConfiguration } from "~hooks/useSportConfiguration.js";
import { Genders, Statuses } from "~utilities/constants.js";
import TeamListFilter from "./TeamListFilter";
import { useExportEntities, useImportEntities } from "~pages/Entities/hooks/index.ts";
import { ImportModal } from "~components/ImportButton/ImportModal.tsx";
import { ExportModal } from "~components/ExportModal/ExportModal.tsx";
import { defaultEntitiesExportFieldNames, entitiesExportFields } from "~pages/Entities/constants/exportFields.ts";

const columnsone = ({ customerId, sport, organizationId, formatMessage, handleOpenConfirmation, PermissionName }) => {
  return [
    {
      title: formatMessage({
        id: "name.local.full",
      }),
      render: (value, row) => (
        <Space size="small">
          <Avatar
            src={row.images?.find((image) => image?.imageType === "LOGO")?.url}
            size={25}
            loading="lazy"
            shape="square"
            alt="Team logo"
          />
          {value}
        </Space>
      ),
      dataIndex: "nameFullLocal",
    },
    {
      title: formatMessage({
        id: "team.id",
        defaultMessage: "Team Id",
      }),
      dataIndex: "entityId",
    },
    {
      title: formatMessage({
        id: "ageGroup",
      }),
      dataIndex: "ageGroup",
    },
    {
      title: formatMessage({
        id: "gender",
        defaultMessage: "Gender",
      }),
      dataIndex: "gender",
    },
    {
      title: formatMessage({
        id: "discipline",
        defaultMessage: "discipline",
      }),
      dataIndex: "discipline",
    },
    {
      title: formatMessage({
        id: "standard",
        defaultMessage: "Standard",
      }),
      dataIndex: "standard",
    },
    {
      title: formatMessage({
        id: "status",
        defaultMessage: "Status",
      }),
      dataIndex: "status",
    },
    {
      width: 120,
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.UPDATE ]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/entities/manage/${record.entityId}/`}>
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.DELETE ]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`} />
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
    },
  ];
};

const statusFilter = [
  {
    text: "Active Teams",
    value: Statuses.ACTIVE,
  },
  {
    text: "Inactive Teams",
    value: Statuses.INACTIVE,
  },
  {
    text: "Pending Teams",
    value: Statuses.PENDING,
  },
];

const GenderFilter = [
  {
    text: Genders.FEMALE,
    value: Genders.FEMALE,
  },
  {
    text: Genders.MALE,
    value: Genders.MALE,
  },
  {
    text: Genders.MIXED,
    value: Genders.MIXED,
  },
  {
    text: Genders.UNKNOWN,
    value: Genders.UNKNOWN,
  },
];

const TeamsList = ({ columns = null, defaultPageSize }) => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, customerId } = useParams();
  const { state } = useLocation();
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(defaultPageSize || 1000);
  const { entityStandards, ageGroups, disciplines } = useSportConfiguration();
  const [ selected, setSelected ] = useState({});
  const [ showConfirmation, setShowConfirmation ] = useState(false);
  const [ searchText, setSearchText ] = useState(state?.entityName || "");
  const [ activeFilter, setActiveFilter ] = useState({
    status: state?.status || "ACTIVE",
    standard: "ALL",
    gender: "ALL",
    ageGroup: "ALL",
    discipline: "ALL",
  });
  const { mutate: deleteFn, isPending } = useDeleteTeam();
  const { data, isLoading } = useFetchTeams({
    limit: pageSize,
    offset: offset,
    ...(searchText?.length > 1 ? { nameFullLocalContains: searchText } : { nameFullLocalContains: "" }),
    ...(activeFilter?.status && activeFilter.status !== "ALL" ? { status: activeFilter.status } : {}),
    ...(activeFilter?.standard && activeFilter.standard !== "ALL" ? { standard: activeFilter.standard } : {}),
    ...(activeFilter?.gender && activeFilter.gender !== "ALL" ? { gender: activeFilter.gender } : {}),
    ...(activeFilter?.ageGroup && activeFilter.ageGroup !== "ALL" ? { ageGroup: activeFilter.ageGroup } : {}),
    ...(activeFilter?.discipline && activeFilter.discipline !== "ALL" ? { discipline: activeFilter.discipline } : {}),
  });

  const { mutateAsync: startImporting } = useImportEntities();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportEntities();

  const StandardsFilter = useMemo(() => {
    return [ ...entityStandards.map((item) => ({ text: item, value: item })) ];
  }, [ entityStandards ]);

  const AgeGroupFilter = useMemo(() => {
    return [ ...ageGroups.map((item) => ({ text: item, value: item })) ];
  }, [ ageGroups ]);

  const DisciplineFilter = useMemo(() => {
    return [ ...disciplines.map((item) => ({ text: item, value: item })) ];
  }, [ disciplines ]);

  const teams = data?.data || [];
  const nextLink = data?.links?.next;
  const { entityPermissionName: PermissionName } = usePermissionsByCompetitionName();

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [ setSelected, setShowConfirmation ],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selected?.entityId);
  }, [ deleteFn, selected?.entityId ]);

  const updateSearch = useMemo(() => debounce((searchText) => setSearchText(searchText), 500), [ setSearchText ]);

  const handleChangeFilter = useCallback((key, value) => {
    setActiveFilter((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  }, []);

  return (
    <Flex vertical gap={8}>
      <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.DELETE ]}>
        <ConfirmationModal
          title="Delete Team"
          setIsModalOpen={setShowConfirmation}
          isModalOpen={showConfirmation}
          handleOk={handleDelete}
          handleCancel={() => {
            setSelected({});
          }}
          okText="Delete"
        >
          <div>{`You are about to delete ${selected.nameFullLocal}. Is this OK?`}</div>
        </ConfirmationModal>
      </PermissionCheck>
      <Flex align="center" justify="space-between">
        <Typography.Title level={4}>Teams</Typography.Title>
        <Space>
          {/*TODO: Uncomment after release*/}
          {/*<PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.CREATE, PermissionOperations.UPDATE ]}>*/}
          {/*  <ImportModal*/}
          {/*    title={formatMessage({ id: "import.modal.title.entities" })}*/}
          {/*    description={formatMessage({ id: "import.modal.text.entities" })}*/}
          {/*    startImporting={startImporting}*/}
          {/*  />*/}
          {/*</PermissionCheck>*/}
          {/*<PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.READ ]}>*/}
          {/*  <ExportModal*/}
          {/*    handleExport={startExport}*/}
          {/*    defaultFields={defaultEntitiesExportFieldNames}*/}
          {/*    groupedFields={entitiesExportFields}*/}
          {/*    localStorageKey="export.entities"*/}
          {/*    loading={isExportPending}*/}
          {/*  />*/}
          {/*</PermissionCheck>*/}
          <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.CREATE ]}>
            <Link
              style={{ marginLeft: "auto" }}
              to={`/${customerId}/admin/${sport}/${organizationId}/entities/manage/`}
              state={{ from: location.pathname }}
            >
              <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>}>
                Team
              </Button>
            </Link>
          </PermissionCheck>
        </Space>
      </Flex>
      <Flex align="center" gap={8}>
        <TeamListFilter
          title={formatMessage({ id: "standard" })}
          value={activeFilter?.standard}
          handleChange={(value) => handleChangeFilter("standard", value)}
          options={[ { text: "ALL", value: "ALL" }, ...StandardsFilter ]}
        />
        <TeamListFilter
          title={formatMessage({ id: "gender" })}
          value={activeFilter?.gender}
          handleChange={(value) => handleChangeFilter("gender", value)}
          options={[ { text: "ALL", value: "ALL" }, ...GenderFilter ]}
        />
        <TeamListFilter
          title={formatMessage({ id: "ageGroup" })}
          value={activeFilter?.ageGroup}
          handleChange={(value) => handleChangeFilter("ageGroup", value)}
          options={[ { text: "ALL", value: "ALL" }, ...AgeGroupFilter ]}
        />
        <TeamListFilter
          title={formatMessage({ id: "discipline" })}
          value={activeFilter?.discipline}
          handleChange={(value) => handleChangeFilter("discipline", value)}
          options={[ { text: "ALL", value: "ALL" }, ...DisciplineFilter ]}
        />
        <TeamListFilter
          title={formatMessage({ id: "status" })}
          value={activeFilter?.status}
          handleChange={(value) => handleChangeFilter("status", value)}
          options={[ { text: "ALL", value: "ALL" }, ...statusFilter ]}
        />
        <SearchFilter
          searchValue={searchText}
          doSearch={updateSearch}
          placeholder={formatMessage({
            id: "person.search.placeholder.2",
            defaultMessage: "Search (2 letters min)",
          })}
        />
      </Flex>
      <Table
        loading={isPending || isLoading}
        size={"small"}
        columns={
          columns
            ? columns
            : columnsone({
              customerId,
              sport,
              organizationId,
              formatMessage,
              handleOpenConfirmation,
              PermissionName,
            })
        }
        rowKey={"entityId"}
        dataSource={teams}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + teams.length + 1 : teams.length + offset,
          position: [ "bottomCenter" ],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
          ),
        }}
      />
    </Flex>
  );
};

export default TeamsList;
